import { useCallback, useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { useAppSelector, useAppDispatch } from "../../store/hooks";
import {
  selectSelectedBricksList,
  selectSelectedBricks,
  selectBrickSize,
  selectStructure,
  selectConfig,
  selectScale,
  addBrick,
  removeBrick,
  changeRate,
  setBrickSize,
  setStructure,
  setHorizontalJointSize,
  setVerticalJointSize,
} from "../../store/slices/configurationSlice";
import { prepareSeries } from "../../store/slices/bricksSlice";
import { SelectedStoneRow } from "./SelectedStoneRow";
import BrickPicker from "../BrickPicker";
import { BrickSeries } from "../../store/slices/bricksSlice";
import {
  BrickSizes,
  brickSizes,
  structures,
  Joint,
  JointSize,
  StructureType,
} from "../../constants";
import JointPicker from "../JointPicker";
import { formatScale, ScaleSlider } from "../ScaleSlider";
import { WallActions } from "../WallActions";

const JOINT_SIZE_LABELS: [JointSize, string][] = [
  [JointSize.Large, "Normaal"],
  [JointSize.Small, "Dun"],
  [JointSize.Glued, "Verlijmd"],
];

const Field: React.FC<{ id: string; label: string; aside?: string; children: React.ReactNode }> = ({
  id,
  label,
  aside,
  children,
}) => (
  <div className="grid min-w-0 gap-1.5">
    <label htmlFor={id} className="flex items-center justify-between text-sm font-medium">
      {label}
      {aside && <span className="font-normal text-muted-foreground tabular-nums">{aside}</span>}
    </label>
    {children}
  </div>
);

const JointSizeSelect: React.FC<{
  id: string;
  value: JointSize;
  onChange(size: JointSize): void;
}> = ({ id, value, onChange }) => (
  <Select value={String(value)} onValueChange={(next) => onChange(Number(next) as JointSize)}>
    <SelectTrigger id={id}>
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      {JOINT_SIZE_LABELS.map(([size, label]) => (
        <SelectItem key={size} value={String(size)}>
          {label}
          <span className="text-muted-foreground">{size} mm</span>
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const BrickMenu = () => {
  const dispatch = useAppDispatch();
  const selectedBricksList = useAppSelector(selectSelectedBricksList);
  const selectedBricks = useAppSelector(selectSelectedBricks);
  const brickSize = useAppSelector(selectBrickSize);
  const structure = useAppSelector(selectStructure);
  const config = useAppSelector(selectConfig);
  const scale = useAppSelector(selectScale);
  const [showBrickPicker, setShowBrickPicker] = useState(false);

  const onBrickSelectCallback = useCallback(
    (b: BrickSeries) => {
      dispatch(addBrick(b._id));
      dispatch(prepareSeries(b._id));
      setShowBrickPicker(false);
    },
    [dispatch],
  );

  if (showBrickPicker) {
    return (
      <BrickPicker onSelect={onBrickSelectCallback} onBack={() => setShowBrickPicker(false)} />
    );
  }

  return (
    <>
      <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
        <section className="grid grid-cols-1 gap-2 p-5">
          <div>
            <h2 className="font-semibold">Stenen</h2>
            <p className="text-sm text-muted-foreground">
              {selectedBricksList.length === 0
                ? "Kies een of meer stenen voor je gevel."
                : "Bepaal per steen hoeveel procent van de muur hij vult."}
            </p>
          </div>
          {selectedBricksList.length > 0 && (
            <ul className="min-w-0 divide-y">
              {selectedBricksList.map((b) => (
                <SelectedStoneRow
                  key={b._id}
                  brick={b}
                  rate={selectedBricks[b._id]}
                  onRateInput={(rate) => dispatch(changeRate({ brickId: b._id, rate }))}
                  onDelete={() => dispatch(removeBrick(b._id))}
                />
              ))}
            </ul>
          )}
          <Button
            variant="outline"
            className="h-10 w-full border-dashed"
            onClick={() => setShowBrickPicker(true)}
          >
            <Plus />
            Steen toevoegen
          </Button>
        </section>
        <Separator />
        <section className="grid grid-cols-1 gap-4 p-5">
          <h2 className="font-semibold">Metselwerk</h2>
          <div className="grid grid-cols-2 gap-3">
            <Field id="brick-size" label="Formaat">
              <Select
                value={brickSize}
                onValueChange={(val) => dispatch(setBrickSize(val as BrickSizes))}
              >
                <SelectTrigger id="brick-size">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  {Object.entries(brickSizes).map(([key, { width, height }]) => (
                    <SelectItem key={key} value={key}>
                      {key}
                      <span className="text-muted-foreground tabular-nums">
                        {width} × {height} mm
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field id="structure" label="Verband">
              <Select
                value={structure}
                onValueChange={(val) => dispatch(setStructure(val as StructureType))}
              >
                <SelectTrigger id="structure">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(structures).map(([key, s]) => (
                    <SelectItem key={key} value={key}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field id="horizontal-joint" label="Lintvoeg">
              <JointSizeSelect
                id="horizontal-joint"
                value={config.jointSize[Joint.Horizontal]}
                onChange={(size) => dispatch(setHorizontalJointSize(size))}
              />
            </Field>
            <Field id="vertical-joint" label="Stootvoeg">
              <JointSizeSelect
                id="vertical-joint"
                value={config.jointSize[Joint.Vertical]}
                onChange={(size) => dispatch(setVerticalJointSize(size))}
              />
            </Field>
          </div>
          <Field id="joint-colour" label="Voegkleur">
            <JointPicker id="joint-colour" />
          </Field>
          <Field id="scale" label="Schaal" aside={formatScale(scale)}>
            <ScaleSlider id="scale" />
          </Field>
        </section>
      </div>
      <div className="border-t bg-muted/30 p-4">
        <WallActions />
      </div>
    </>
  );
};

export default BrickMenu;
